<template>
  <div
    class="sidebar-layout__wrapper"
    :class="{ ['no-content']: noContent, [extraClass]: !!extraClass }"
  >
    <the-sidebar
      :mainLinks="getMainLinks"
      :serviceLinks="getServiceLinks"
      :mainIcon="mainLogo"
      :fullPath="$route.fullPath"
    ></the-sidebar>

    <div
      ref="content"
      id="content"
      class="sidebar-layout__content"
      :style="borderRadiusStyles"
    >
      <!-- Need to add this div to fix annoying FF bug with padding-bottom -->
      <!-- Discussion here: https://github.com/w3c/csswg-drafts/issues/129 -->
      <div class="sidebar-layout__content-wrapper">
        <slot></slot>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from "vue";
import VueTypes from "vue-types";
import { mapGetters } from "vuex";
import TheSidebar from "@/components/TheSidebar.vue";
import withCardContent from "@/mixins/withCardContent";

import sepireLogo from "@/assets/images/sepire-logo.svg";
import purpleLogo from "@/assets/images/purple-logo.svg";
import greenLogo from "@/assets/images/green-logo.svg";

//utils
import { isCurrentRoute } from "@/utils/util";

// icons campaign
import CampaignIcon from "@/assets/images/menu-icons-new/campaigns.svg";
import CampaignIconActive from "@/assets/images/menu-icons-new/campaigns-active.svg";
import CampaignInkLogo from "@/assets/images/campaign_logo.svg";

// icons templates
import TemplatesIcon from "@/assets/images/menu-icons-new/templates.svg";
import TemplatesIconActive from "@/assets/images/menu-icons-new/templates-active.svg";
import TemplatesInkLogo from "@/assets/images/mail_logo.svg";

// icons contacts
import ContactsIcon from "@/assets/images/menu-icons-new/contacts.svg";
import ContactsIconActive from "@/assets/images/menu-icons-new/contacts-active.svg";
import ContactsInkLogo from "@/assets/images/logo.svg";

// icons home (dashboard)
import HomeIcon from "@/assets/images/menu-icons-new/home_new.svg";
import HomeIconActive from "@/assets/images/menu-icons-new/home_new-active.svg";

// icons history
import HistoryIcon from "@/assets/images/menu-icons-new/history.svg";
import HistoryIconActive from "@/assets/images/menu-icons-new/history-active.svg";

import DefaultAppLogo from "@/assets/images/app_logo.svg";

// icons docs
import DocsIcon from "@/assets/images/menu-icons-new/docs.svg";
import DocsIconActive from "@/assets/images/menu-icons-new/docs-active.svg";

//icons support
import SupportIcon from "@/assets/images/menu-icons-new/support.svg";
import SupportIconActive from "@/assets/images/menu-icons-new/support-active.svg";

export default Vue.component("SidebarLayout", {
  props: {
    noContent: VueTypes.bool.def(false),
    extraClass: VueTypes.string.def("")
  },

  components: {
    TheSidebar
  },

  mixins: [withCardContent("content")],

  data() {
    return {
      mainLogo: sepireLogo
    };
  },

  computed: {
    getCurrentIcon() {
      const routeName = this.$route.name;

      if (routeName.includes("campaign")) {
        return CampaignInkLogo;
      } else if (routeName.includes("template")) {
        return TemplatesInkLogo;
      } else if (routeName.includes("contact")) {
        return ContactsInkLogo;
      } else if (routeName.includes("dashboard")) {
        return ContactsInkLogo;
      }

      return DefaultAppLogo;
    },

    getMainLinks() {
      const { fullPath } = this.$route;
      return [
        {
          key: "dashboard",
          url: "/dashboard",
          label: "Home",
          active: !!this.isCurrentRoute(fullPath, "dashboard"),
          icon: HomeIcon,
          activeIcon: HomeIconActive
        },
        {
          key: "campaign",
          url: "/campaigns",
          label: "Campaigns",
          active: !!this.isCurrentRoute(fullPath, "campaigns"),
          icon: CampaignIcon,
          activeIcon: CampaignIconActive
        },
        {
          key: "templates",
          url: "/templates",
          label: "Templates",
          active:
            !!this.isCurrentRoute(fullPath, "templates") ||
            !!this.isCurrentRoute(fullPath, "letters-templates"),
          icon: TemplatesIcon,
          activeIcon: TemplatesIconActive
        },
        {
          key: "history",
          url: "/history/postcards",
          label: "History",
          active: !!this.isCurrentRoute(fullPath, "history"),
          icon: HistoryIcon,
          activeIcon: HistoryIconActive
        },
        {
          key: "contacts",
          url: "/contacts",
          label: "Contacts",
          active: !!this.isCurrentRoute(fullPath, "contacts"),
          icon: ContactsIcon,
          activeIcon: ContactsIconActive
        }
      ];
    },

    getServiceLinks() {
      const { fullPath } = this.$route;
      return [
        {
          key: "docs",
          externalUrl: true,
          url: "https://docs.inkit.com/docs",
          label: "Docs",
          icon: DocsIcon,
          activeIcon: DocsIconActive
        },
        {
          key: "support",
          url: "/support",
          label: "Support",
          icon: SupportIcon,
          active: !!this.isCurrentRoute(fullPath, "support"),
          activeIcon: SupportIconActive
        }
      ];
    },
    ...mapGetters("billing", ["cards"])
  },

  methods: {
    isCurrentRoute(fullPath, name) {
      const re = new RegExp(`/${name}`);
      return re.exec(fullPath);
    }
  }
});
</script>

<style lang="scss" scoped>
$ns: "sidebar-layout";

.#{$ns} {
  &__wrapper {
    display: flex;
    height: 100vh;
    background-color: #ebeef0;
    padding-left: $sidebar-width;

    &.no-content {
      width: $sidebar-width;

      .#{$ns}__content {
        display: none;
      }
    }
  }

  &__content {
    flex: 1;
    max-height: 100vh;
    padding: $general-padding 20px 0 40px;
    overflow-y: auto;
    overflow-x: hidden;
    background-color: $main-body-color;
    height: 100%;
    box-shadow: 0 0 30px rgba(124, 140, 163, 0.3);
  }

  &__content-wrapper {
    /*padding-bottom: 20px;*/
    height: 100%;
  }
}

@media screen and (max-width: $tablet-screen),  screen and (max-height: $small-screen){
  .#{$ns} {
    &__content {
      padding: 95px 20px 30px 20px;
    }

    &__wrapper {
      padding-left: 0;

      &.no-content {
        width: 0;
      }
    }
  }
}
</style>
